import React, { forwardRef, memo } from 'react';

import FocusRing from '@atlaskit/focus-ring';
import { type XCSS, xcss } from '@atlaskit/primitives';

import { TR, type TRProps } from '../../ui/primitives/tr';
import { type RowSpacing } from '../charlie-table';

interface RowProps extends TRProps {
	spacing?: RowSpacing;
}
const bodyRowStyles: XCSS = xcss({
	height: '44px',
	':hover': {
		backgroundColor: 'elevation.surface.raised.hovered',
	},
	':focus-visible::after': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		boxShadow: undefined,
	},
	position: 'relative',
	border: 'none',
	borderImageWidth: 0,
	borderSpacing: 0,
	backgroundColor: 'elevation.surface',
});

const compactRow = xcss({
	height: '36px', //replace with size tokens when released
});

/**
 * __Row__
 *
 * A table row.
 *
 * - [Examples](https://atlassian.design/components/table/examples)
 */
export const Row = memo(
	forwardRef<HTMLTableRowElement, RowProps>(
		({ children, testId, spacing, xcss: customXcss, isSelected, ...props }, ref) => {
			return (
				<FocusRing isInset>
					<TR
						ref={ref}
						isSelected={isSelected}
						testId={testId}
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- this is inline with what atlaskit primitives do.
						xcss={[bodyRowStyles, spacing === 'compact' && compactRow, customXcss as XCSS]}
						{...props}
					>
						{children}
					</TR>
				</FocusRing>
			);
		},
	),
);
